import React, { useEffect, useState, useRef } from "react";
import { createPortal } from "react-dom";
import { useParams } from "react-router-dom";
import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  collection,
  getDocs,
  deleteDoc,
} from "firebase/firestore";
import { db, storage, auth } from "../../../db/firebase";
import { v4 as uuidv4 } from "uuid";
import {
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";
import { useTranslation } from "react-i18next";
import {
  HiOutlineXMark,
  HiOutlinePaperClip,
  HiOutlineTv,
  HiOutlinePencilSquare,
  HiOutlinePlus,
  HiOutlineMinus,
  HiOutlineTrash,
} from "react-icons/hi2";
import { transposeChords } from "../../../utils/transposeChords";
import { ChordChartWithHover } from "../../../components/ChordChartWithHover";
import { LoadingPage } from "../../../components/Loading";
import { AnimatedModal } from "../../../components/AnimatedModal";
import { SongArtwork } from "../../../components/SongArtwork";
import { YoutubeVideosSection } from "../../../components/YoutubeVideosSection";
import PresentationMode from "../Events/Components/PresentationMode";
import { useAuth } from "../../../context/AuthContext";
import { getYouTubeEmbedUrl, getYoutubeLinksFromSong, isYouTubeUrl } from "../../../utils/youtubeLinks";

interface Comment {
  id: string;
  userName: string;
  comment: string;
  timestamp: string;
  attachmentUrl?: string;
  attachmentType?: string;
  attachmentName?: string;
}

interface ChordVersion {
  id: string;
  label: string;
  content: string;
}

const MAX_FILE_SIZE_MB = 5;

const ViewSong: React.FC = () => {
  const { t } = useTranslation(); // Initialize translation
  const { organizationId } = useAuth();
  const { id } = useParams<{ id: string }>();
  const [song, setSong] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [newComment, setNewComment] = useState<string>("");
  const [comments, setComments] = useState<Comment[]>([]);
  const [attachment, setAttachment] = useState<File | null>(null);
  const [attachmentError, setAttachmentError] = useState<string>("");
  const [chords, setChords] = useState<string>("");
  const [chordVersions, setChordVersions] = useState<ChordVersion[]>([]);
  const [selectedVersionIndex, setSelectedVersionIndex] = useState(0);
  const [chordsEditing, setChordsEditing] = useState(false);
  const [chordsSaving, setChordsSaving] = useState(false);
  const [transposeSemitones, setTransposeSemitones] = useState(0);
  const [isNotesModalOpen, setIsNotesModalOpen] = useState(false);
  const [isChordsModalOpen, setIsChordsModalOpen] = useState(false);
  const [chordsModalMode, setChordsModalMode] = useState<"view" | "edit">("view");
  const [showPresentationMode, setShowPresentationMode] = useState(false);

  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const handleTranspose = (delta: number) => {
    const newVal = transposeSemitones + delta;
    if (newVal < -11 || newVal > 11) return;
    setTransposeSemitones(newVal);
    setChords((prev) => transposeChords(prev, delta));
  };

  // Metronome States
  const [bpm, setBpm] = useState<number>(120); // Default BPM for the metronome
  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const audioContextRef = useRef<AudioContext | null>(null);
  const timerRef = useRef<number | null>(null);

  useEffect(() => {
    const fetchSong = async () => {
      if (id) {
        const songDoc = await getDoc(doc(db, "songs", id));
        if (songDoc.exists()) {
          const songData = songDoc.data();
          setSong(songData);
          const versions = songData.chordVersions;
          if (versions && Array.isArray(versions) && versions.length > 0) {
            setChordVersions(versions);
            setSelectedVersionIndex(0);
            setChords(versions[0]?.content || "");
          } else {
            setChordVersions([]);
            setChords(songData.chords || songData.charts || "");
          }
          setTransposeSemitones(0);

          const user = auth.currentUser;
          if (user) {
            const userDoc = await getDoc(doc(db, "users", user.uid));
            if (userDoc.exists()) {
              const orgId = userDoc.data().organizationId;
              const commentsRef = collection(
                db,
                "organizations",
                orgId,
                "songs",
                id,
                "comments"
              );
              const commentsSnapshot = await getDocs(commentsRef);
              const commentsData = commentsSnapshot.docs.map((doc) => ({
                id: doc.id,
                ...doc.data(),
              })) as Comment[];
              setComments(commentsData);
            }
          }
        }
        setLoading(false);
      }
    };

    fetchSong();
  }, [id]);

  const selectedVersionContent =
    chordVersions.length > 0
      ? chordVersions[selectedVersionIndex]?.content
      : undefined;

  useEffect(() => {
    if (selectedVersionContent !== undefined) {
      setChords(selectedVersionContent);
      setTransposeSemitones(0);
    }
  }, [selectedVersionIndex, selectedVersionContent]);

  const handleRemoveChordVersion = () => {
    const newVersions = chordVersions.filter((_, i) => i !== selectedVersionIndex);
    setChordVersions(newVersions);
    const newIndex = Math.min(selectedVersionIndex, Math.max(0, newVersions.length - 1));
    setSelectedVersionIndex(newIndex);
    setChords(newVersions[newIndex]?.content || "");
  };

  const handleAddChordVersion = () => {
    const newVersion: ChordVersion = {
      id: uuidv4(),
      label: "",
      content: "",
    };
    setChordVersions((prev) => {
      if (prev.length === 0 && chords.trim()) {
        return [
          { id: uuidv4(), label: "Chords", content: chords },
          newVersion,
        ];
      }
      return [...prev, newVersion];
    });
    setSelectedVersionIndex(chordVersions.length === 0 && chords.trim() ? 1 : chordVersions.length);
    setChords("");
  };

  const handleAddComment = async () => {
    if (newComment.trim() !== "" || attachment) {
      let attachmentUrl = "";
      let attachmentType = "";
      let attachmentName = "";

      if (attachment) {
        if (attachment.size / (1024 * 1024) > MAX_FILE_SIZE_MB) {
          setAttachmentError(
            t("fileSizeError", { maxFileSize: MAX_FILE_SIZE_MB })
          );
          return;
        }

        const storageRef = ref(storage, `attachments/${uuidv4()}`);
        await uploadBytes(storageRef, attachment);
        attachmentUrl = await getDownloadURL(storageRef);
        attachmentType = attachment.type.split("/")[0];
        attachmentName = attachment.name;
        setAttachment(null);
        setAttachmentError("");
      }

      const user = auth.currentUser;
      let userName = t("anonymousUser");
      if (user) {
        const userDoc = await getDoc(doc(db, "users", user.uid));
        if (userDoc.exists()) {
          userName = `${userDoc.data().firstName} ${userDoc.data().lastName}`;
        }
      }

      const newCommentObject: Comment = {
        id: uuidv4(),
        userName,
        comment: newComment,
        timestamp: new Date().toISOString(),
        attachmentUrl,
        attachmentType,
        attachmentName,
      };
      const updatedComments = [newCommentObject, ...comments];
      setComments(updatedComments);
      setNewComment("");

      if (id && user) {
        const userDoc = await getDoc(doc(db, "users", user.uid));
        if (userDoc.exists()) {
          const orgId = userDoc.data().organizationId;
          const commentsRef = collection(
            db,
            "organizations",
            orgId,
            "songs",
            id,
            "comments"
          );
          await setDoc(doc(commentsRef, newCommentObject.id), newCommentObject);
        }
      }

      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleSaveChords = async () => {
    if (!id) return;
    setChordsSaving(true);
    try {
      let versionsToSave = chordVersions;
      if (chordVersions.length > 0) {
        versionsToSave = chordVersions.map((v, i) =>
          i === selectedVersionIndex ? { ...v, content: chords } : v
        );
        setChordVersions(versionsToSave);
      } else {
        versionsToSave = [{ id: uuidv4(), label: "Chords", content: chords }];
        setChordVersions(versionsToSave);
      }
      const chordsForCompat = versionsToSave[0]?.content || chords;
      await updateDoc(doc(db, "songs", id), {
        chords: chordsForCompat,
        chordVersions: versionsToSave,
      });
      setSong((prev: any) =>
        prev ? { ...prev, chords: chordsForCompat, chordVersions: versionsToSave } : null
      );
      setChordsEditing(false);
    } catch (err) {
      console.error("Error saving chords:", err);
    } finally {
      setChordsSaving(false);
    }
  };

  const handleDeleteComment = async (commentId: string) => {
    const commentToDelete = comments.find(
      (comment) => comment.id === commentId
    );
    if (commentToDelete && commentToDelete.attachmentUrl) {
      const attachmentRef = ref(storage, commentToDelete.attachmentUrl);
      try {
        await deleteObject(attachmentRef);
      } catch (error) {
        console.error("Error deleting attachment: ", error);
      }
    }

    const updatedComments = comments.filter(
      (comment) => comment.id !== commentId
    );
    setComments(updatedComments);

    if (id) {
      const user = auth.currentUser;
      if (user) {
        const userDoc = await getDoc(doc(db, "users", user.uid));
        if (userDoc.exists()) {
          const orgId = userDoc.data().organizationId;
          const commentsRef = collection(
            db,
            "organizations",
            orgId,
            "songs",
            id,
            "comments"
          );
          await deleteDoc(doc(commentsRef, commentId));
        }
      }
    }
  };

  // Metronome Logic
  const startMetronome = () => {
    if (!audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext ||
        (window as any).webkitAudioContext)();
    }

    const playClick = () => {
      const osc = audioContextRef.current!.createOscillator();
      const gain = audioContextRef.current!.createGain();
      osc.type = "triangle"; // Simple sine wave click
      osc.frequency.setValueAtTime(1000, audioContextRef.current!.currentTime);
      gain.gain.setValueAtTime(1, audioContextRef.current!.currentTime);
      osc.connect(gain);
      gain.connect(audioContextRef.current!.destination);
      osc.start();
      osc.stop(audioContextRef.current!.currentTime + 0.05); // Short beep
    };

    // Calculate interval in milliseconds
    const interval = (60 / bpm) * 1000;

    playClick();
    timerRef.current = window.setInterval(playClick, interval);
  };

  const stopMetronome = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  const toggleMetronome = () => {
    if (isPlaying) {
      stopMetronome();
    } else {
      startMetronome();
    }
    setIsPlaying(!isPlaying);
  };

  const isValidUrl = (url: string) => {
    try {
      new URL(url);
      return true;
    } catch (error) {
      return false;
    }
  };

  if (loading) {
    return <LoadingPage message={t("loading")} />;
  }

  if (!song) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="bg-white dark:bg-gray-800 p-8 rounded-2xl shadow-md w-full max-w-md text-center border border-gray-100 dark:border-gray-700">
          <p className="text-gray-700 dark:text-gray-200">{t("songNotFound")}</p>
        </div>
      </div>
    );
  }

  const songYoutubeLinks = getYoutubeLinksFromSong(song);

  return (
    <div className="w-full">
      <div className="mx-auto w-full max-w-7xl">
        <div className="flex flex-col lg:flex-row gap-6">
          <div className="flex-1 min-w-0 space-y-4">
            <div className="bg-white dark:bg-gray-800 flex rounded-2xl p-5 shadow-sm border border-gray-100 dark:border-gray-700">
            <div>
              <SongArtwork
                src={song.artworkURL}
                alt={song.title}
                className="rounded-md w-32 h-32"
                iconClassName="w-12 h-12"
              />
            </div>
            <div className="ml-4 min-w-0">
              <h1 className="text-2xl font-bold mb-2 text-gray-800 dark:text-gray-100">
                {song.title}
              </h1>
              <p className="text-gray-600 dark:text-gray-300 mb-2">
                <strong>{t("artist")}:</strong> {song.artist}
              </p>
              <p className="text-gray-600 dark:text-gray-300 mb-2">
                <strong>{t("album")}:</strong> {song.album}
              </p>
            </div>
          </div>
          {/* Chords section */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-700 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="flex items-center gap-2">
                <div className="w-1 h-5 rounded-full bg-gradient-to-b from-[#5932EA] to-[#7C3AED]" />
                <h2 className="text-lg font-bold text-gray-800 dark:text-gray-100">
                  {t("chords") || "Chords"}
                </h2>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                {chords && (
                  <>
                    <div className="flex items-center gap-1 bg-gray-100 dark:bg-gray-700 rounded-lg px-2 py-1.5">
                      <button
                        type="button"
                        onClick={() => handleTranspose(-1)}
                        disabled={transposeSemitones <= -11}
                        className="w-7 h-7 rounded-md flex items-center justify-center text-gray-600 dark:text-gray-400 hover:bg-white dark:hover:bg-gray-600 hover:text-[#5932EA] disabled:opacity-40 transition-colors"
                        aria-label={t("transpose") || "Transpose down"}
                      >
                        <HiOutlineMinus className="w-4 h-4" />
                      </button>
                      <span className="min-w-[2rem] text-center text-sm font-semibold text-gray-800 dark:text-gray-200">
                        {transposeSemitones >= 0 ? `+${transposeSemitones}` : transposeSemitones}
                      </span>
                      <button
                        type="button"
                        onClick={() => handleTranspose(1)}
                        disabled={transposeSemitones >= 11}
                        className="w-7 h-7 rounded-md flex items-center justify-center text-gray-600 dark:text-gray-400 hover:bg-white dark:hover:bg-gray-600 hover:text-[#5932EA] disabled:opacity-40 transition-colors"
                        aria-label={t("transpose") || "Transpose up"}
                      >
                        <HiOutlinePlus className="w-4 h-4" />
                      </button>
                    </div>
                    <button
                      onClick={() => setShowPresentationMode(true)}
                      className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium bg-[#5932EA] text-white hover:bg-[#4a28c7] transition-colors"
                    >
                      <HiOutlineTv className="w-4 h-4" />
                      {t("view") || "View"}
                    </button>
                  </>
                )}
                <button
                  onClick={() => {
                    if (transposeSemitones !== 0) {
                      setChords(transposeChords(chords, transposeSemitones));
                      setTransposeSemitones(0);
                    }
                    setChordsEditing(true);
                    setChordsModalMode("edit");
                    setIsChordsModalOpen(true);
                  }}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    chords
                      ? "bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-[#5932EA]/10 hover:text-[#5932EA] dark:hover:bg-[#5932EA]/20 dark:hover:text-[#5932EA]"
                      : "bg-[#5932EA] text-white hover:bg-[#4a28c7]"
                  }`}
                >
                  <HiOutlinePencilSquare className="w-4 h-4" />
                  {chords ? (t("edit") || "Edit") : (t("addChords") || "Add chords")}
                </button>
              </div>
            </div>
            {chords ? (
              <div className="p-5">
                {chordVersions.length > 1 && (
                  <div className="flex flex-wrap gap-1.5 mb-3">
                    {chordVersions.map((v, i) => (
                      <button
                        key={v.id}
                        type="button"
                        onClick={() => setSelectedVersionIndex(i)}
                        className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                          selectedVersionIndex === i
                            ? "bg-[#5932EA] text-white"
                            : "bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600"
                        }`}
                      >
                        {v.label || `Version ${i + 1}`}
                      </button>
                    ))}
                  </div>
                )}
                <div className="text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-700/50 p-5 rounded-xl max-h-80 overflow-y-auto border border-gray-100 dark:border-gray-600 font-mono text-[15px] leading-relaxed">
                  <ChordChartWithHover
                    text={transposeChords(chords, transposeSemitones)}
                    className="text-gray-700 dark:text-gray-300"
                  />
                </div>
              </div>
            ) : (
              <div className="p-8 text-center">
                <div className="w-14 h-14 rounded-xl bg-gray-100 dark:bg-gray-700 flex items-center justify-center mx-auto mb-3">
                  <HiOutlinePencilSquare className="w-7 h-7 text-gray-400 dark:text-gray-500" />
                </div>
                <p className="text-gray-500 dark:text-gray-400 text-sm mb-1">
                  {t("noChordsYet") || "No chords added yet."}
                </p>
                <p className="text-gray-400 dark:text-gray-500 text-xs mb-4">
                  {t("noChordsYetHint") || "Click \"Add chords\" to add chord charts or chord progressions."}
                </p>
                <button
                  onClick={() => {
                    setChordsEditing(true);
                    setChordsModalMode("edit");
                    setIsChordsModalOpen(true);
                  }}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-[#5932EA] text-white hover:bg-[#4a28c7] transition-colors"
                >
                  <HiOutlinePlus className="w-4 h-4" />
                  {t("addChords") || "Add chords"}
                </button>
              </div>
            )}
          </div>

          {/* Chords Modal - Full view */}
          <AnimatedModal
            isOpen={isChordsModalOpen}
            backdropClassName="!p-0 md:!p-4 !items-stretch !justify-stretch md:!items-center md:!justify-center bg-black/60 backdrop-blur-sm"
            contentClassName="w-full h-full min-w-full min-h-[100dvh] md:w-auto md:h-auto md:min-w-0 md:min-h-0"
            onClose={() => {
              if (chordsEditing) {
                if (chordVersions.length > 0 && chordVersions[selectedVersionIndex]) {
                  setChords(chordVersions[selectedVersionIndex].content);
                } else {
                  setChords(song?.chords || song?.charts || "");
                }
                setTransposeSemitones(0);
                setChordsEditing(false);
              }
              setIsChordsModalOpen(false);
            }}
          >
            <div
              className={`bg-white dark:bg-gray-800 shadow-xl flex flex-col overflow-hidden w-full h-[100dvh] md:h-[90vh] md:min-h-[90vh] md:w-auto md:max-w-[calc(100vw-2rem)] md:rounded-xl m-0 md:mx-auto ${
                chordsModalMode === "view"
                  ? "md:max-w-3xl lg:max-w-5xl xl:max-w-6xl 2xl:max-w-7xl"
                  : "md:max-w-5xl lg:max-w-[85vw] xl:max-w-[90vw]"
              }`}
            >
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 sm:gap-4 px-4 sm:px-6 py-3 sm:py-4 bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-600 shrink-0">
                <div className="flex flex-wrap items-center gap-2">
                  <h3 className="text-base sm:text-lg font-semibold text-gray-800 dark:text-gray-100 shrink-0">{t("chords") || "Chords"}</h3>
                  {chordVersions.length > 1 && (
                    <div className="flex flex-wrap gap-1">
                      {chordVersions.map((v, i) => (
                        <button
                          key={v.id}
                          type="button"
                          onClick={() => setSelectedVersionIndex(i)}
                          className={`px-2.5 py-1 rounded-lg text-xs font-medium transition-colors ${
                            selectedVersionIndex === i
                              ? "bg-[#5932EA] text-white"
                              : "bg-white dark:bg-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-500"
                          }`}
                        >
                          {v.label || `V${i + 1}`}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex flex-wrap items-center justify-end gap-2 sm:gap-3">
                  {chordsModalMode === "view" && chords ? (
                    <div className="flex items-center gap-1 bg-white dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600 px-2 py-1.5 shadow-sm">
                      <span className="text-xs text-gray-500 dark:text-gray-400 mr-1">{t("transpose")}:</span>
                      <button
                        type="button"
                        onClick={() => handleTranspose(-1)}
                        disabled={transposeSemitones <= -11}
                        className="w-8 h-8 rounded-md border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-500 disabled:opacity-40 font-medium text-sm transition-colors"
                      >
                        −
                      </button>
                      <span className="min-w-[2.25rem] text-center text-sm font-semibold text-gray-800 dark:text-gray-200">
                        {transposeSemitones >= 0 ? `+${transposeSemitones}` : transposeSemitones}
                      </span>
                      <button
                        type="button"
                        onClick={() => handleTranspose(1)}
                        disabled={transposeSemitones >= 11}
                        className="w-8 h-8 rounded-md border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-500 disabled:opacity-40 font-medium text-sm transition-colors"
                      >
                        +
                      </button>
                    </div>
                  ) : chordsEditing ? (
                    <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                      <div className="flex items-center gap-0.5 sm:gap-1 bg-white dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600 px-1.5 sm:px-2 py-1 sm:py-1.5 shadow-sm">
                        <span className="text-xs text-gray-500 dark:text-gray-400 mr-0.5 sm:mr-1 hidden sm:inline">{t("transpose")}:</span>
                        <button
                          type="button"
                          onClick={() => handleTranspose(-1)}
                          disabled={transposeSemitones <= -11}
                          className="w-7 h-7 sm:w-8 sm:h-8 rounded-md border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-500 disabled:opacity-40 font-medium text-sm transition-colors touch-manipulation"
                        >
                          −
                        </button>
                        <span className="min-w-[1.75rem] sm:min-w-[2.25rem] text-center text-xs sm:text-sm font-semibold text-gray-800 dark:text-gray-200">
                          {transposeSemitones >= 0 ? `+${transposeSemitones}` : transposeSemitones}
                        </span>
                        <button
                          type="button"
                          onClick={() => handleTranspose(1)}
                          disabled={transposeSemitones >= 11}
                          className="w-7 h-7 sm:w-8 sm:h-8 rounded-md border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-500 disabled:opacity-40 font-medium text-sm transition-colors touch-manipulation"
                        >
                          +
                        </button>
                      </div>
                      <button
                        onClick={handleAddChordVersion}
                        className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-[#5932EA] hover:bg-[#5932EA]/10 dark:hover:bg-[#5932EA]/20 rounded-lg transition-colors border border-[#5932EA]/30 dark:border-[#5932EA]/50 touch-manipulation"
                      >
                        <HiOutlinePlus className="w-4 h-4" />
                        {t("addChordVersion") || "Add chord version"}
                      </button>
                      {chordVersions.length > 0 && (
                        <button
                          onClick={handleRemoveChordVersion}
                          className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-red-500 hover:bg-red-500/10 dark:hover:bg-red-500/20 rounded-lg transition-colors border border-red-500/30 dark:border-red-500/50 touch-manipulation"
                          title={t("removeVersion") || "Remove version"}
                        >
                          <HiOutlineTrash className="w-4 h-4" />
                          {t("removeVersion") || "Remove version"}
                        </button>
                      )}
                      <div className="w-px h-5 sm:h-6 bg-gray-200 dark:bg-gray-600 hidden sm:block" aria-hidden />
                      <button
                          onClick={async () => {
                            await handleSaveChords();
                            setChordsEditing(false);
                          }}
                          disabled={chordsSaving}
                          className="px-4 sm:px-5 py-2 bg-[#5932EA] text-white text-sm font-medium rounded-lg hover:bg-[#4a28c7] disabled:opacity-50 transition-colors shadow-sm touch-manipulation"
                        >
                          {chordsSaving ? (t("saving") || "Saving...") : (t("save") || "Save")}
                        </button>
                    </div>
                  ) : (
                    <>
                      {chords && (
                        <div className="flex items-center gap-1 bg-white dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600 px-2 py-1.5 shadow-sm">
                          <span className="text-xs text-gray-500 dark:text-gray-400 mr-1">{t("transpose")}:</span>
                          <button
                            type="button"
                            onClick={() => handleTranspose(-1)}
                            disabled={transposeSemitones <= -11}
                            className="w-8 h-8 rounded-md border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-500 disabled:opacity-40 font-medium text-sm transition-colors"
                          >
                            −
                          </button>
                          <span className="min-w-[2.25rem] text-center text-sm font-semibold text-gray-800 dark:text-gray-200">
                            {transposeSemitones >= 0 ? `+${transposeSemitones}` : transposeSemitones}
                          </span>
                          <button
                            type="button"
                            onClick={() => handleTranspose(1)}
                            disabled={transposeSemitones >= 11}
                            className="w-8 h-8 rounded-md border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-500 disabled:opacity-40 font-medium text-sm transition-colors"
                          >
                            +
                          </button>
                        </div>
                      )}
                      <button
                        onClick={() => {
                          if (transposeSemitones !== 0) {
                            setChords(transposeChords(chords, transposeSemitones));
                            setTransposeSemitones(0);
                          }
                          setChordsEditing(true);
                        }}
                        className="px-3 py-1.5 text-[#5932EA] text-sm font-medium hover:bg-[#5932EA]/10 rounded-lg transition-colors"
                      >
                        {chords ? (t("edit") || "Edit") : (t("addChords") || "Add chords")}
                      </button>
                    </>
                  )}
                  {(chordsModalMode === "view" || chordsEditing) && (
                    <div className="w-px h-5 sm:h-6 bg-gray-200 dark:bg-gray-600 hidden sm:block" aria-hidden />
                  )}
                  <button
                    onClick={() => {
                      if (chordsEditing) {
                        if (chordVersions.length > 0 && chordVersions[selectedVersionIndex]) {
                          setChords(chordVersions[selectedVersionIndex].content);
                        } else {
                          setChords(song?.chords || song?.charts || "");
                        }
                        setTransposeSemitones(0);
                        setChordsEditing(false);
                      }
                      setIsChordsModalOpen(false);
                    }}
                    className="p-2 sm:p-2.5 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-white dark:hover:bg-gray-600 transition-colors touch-manipulation -mr-1"
                    aria-label="Close"
                  >
                    <HiOutlineXMark className="w-5 h-5" />
                  </button>
                </div>
              </div>
              <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden px-4 sm:px-6 py-4 sm:py-5 pb-6 sm:pb-8 bg-white dark:bg-gray-800 [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-gray-50 dark:[&::-webkit-scrollbar-track]:bg-gray-700 [&::-webkit-scrollbar-thumb]:bg-gray-300 dark:[&::-webkit-scrollbar-thumb]:bg-gray-600 [&::-webkit-scrollbar-thumb]:rounded-full">
                {chordsEditing && chordsModalMode === "edit" ? (
                  <div className="h-full min-h-[240px] sm:min-h-[320px] space-y-3">
                    {chordVersions.length > 0 && chordVersions[selectedVersionIndex] && (
                      <input
                        type="text"
                        value={chordVersions[selectedVersionIndex].label}
                        onChange={(e) =>
                          setChordVersions((prev) =>
                            prev.map((v, i) =>
                              i === selectedVersionIndex ? { ...v, label: e.target.value } : v
                            )
                          )}
                        placeholder={t("chordVersionLabel") || "e.g. Guitar, Piano, Bass"}
                        className="w-full max-w-xs px-3 py-2 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-800 dark:text-gray-200 bg-white dark:bg-gray-700 text-sm focus:outline-none focus:ring-2 focus:ring-[#5932EA]/30"
                      />
                    )}
                    <textarea
                      value={chords}
                      onChange={(e) => setChords(e.target.value)}
                      placeholder="e.g. Verse: Am - G - C - F | Chorus: C - G - Am - F"
                      className="w-full h-full min-h-[200px] sm:min-h-[280px] px-4 py-3 sm:py-4 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-800 dark:text-gray-200 font-mono text-[15px] sm:text-[15px] leading-relaxed placeholder-gray-400 dark:placeholder-gray-500 bg-white dark:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-[#5932EA]/30 focus:border-[#5932EA] transition-shadow touch-manipulation"
                      rows={12}
                    />
                  </div>
                ) : chords ? (
                  <div className="text-gray-800 dark:text-gray-200 max-w-2xl sm:max-w-4xl lg:max-w-full mx-auto">
                    <ChordChartWithHover
                      text={transposeChords(chords, transposeSemitones)}
                      className="text-gray-800 dark:text-gray-200 text-base sm:text-[15px] leading-relaxed"
                      chordClassName="text-[#5932EA] decoration-[#5932EA]/40 hover:text-[#4a28c7] hover:decoration-[#4a28c7]"
                    />
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <p className="text-gray-500 dark:text-gray-400 mb-4">{t("noChordsYet")}</p>
                    <button
                      onClick={() => setChordsEditing(true)}
                      className="px-4 py-2 bg-[#5932EA] text-white text-sm font-medium rounded-lg hover:bg-[#4a28c7] transition-colors"
                    >
                      {t("addChords") || "Add chords"}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </AnimatedModal>
          </div>
          <div className="flex-1 min-w-0 space-y-4">
          {id && (
            <YoutubeVideosSection
              songId={id}
              initialLinks={songYoutubeLinks}
              onSaved={(links) =>
                setSong((prev: any) =>
                  prev
                    ? {
                        ...prev,
                        youtubeLinks: links,
                        youtubeLink: links[0] || "",
                      }
                    : null
                )
              }
            />
          )}

          {/* Notes Section */}
          <div className="bg-white dark:bg-gray-800 p-5 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700">
            <div className="flex items-center justify-between gap-2 mb-4">
              <h2 className="text-lg font-bold text-gray-800 dark:text-gray-100">{t("notes")}</h2>
              <button
                onClick={() => setIsNotesModalOpen(true)}
                className="bg-[#5932EA] text-white text-sm px-3 py-1.5 rounded-lg hover:bg-[#4a28c7] transition-colors"
              >
                + {t("addNote") || "Add note"}
              </button>
            </div>
            <div className="max-h-64 overflow-y-auto">
              {comments.length > 0 ? (
                <ul className="space-y-2">
                  {comments.map((comment) => (
                    <li
                      key={comment.id}
                      className="bg-gray-50 dark:bg-gray-900/40 rounded-xl p-3 border border-gray-100 dark:border-gray-700"
                    >
                      <div className="w-full">
                        {isYouTubeUrl(comment.comment) ? (
                          <iframe
                            width="100%"
                            height="140"
                            src={getYouTubeEmbedUrl(comment.comment) || ""}
                            frameBorder="0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                            title="YouTube video"
                            className="rounded-lg"
                          />
                        ) : isValidUrl(comment.comment) ? (
                          <a
                            href={comment.comment}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-[#5932EA] hover:text-[#4a28c7] break-all text-sm"
                          >
                            {comment.comment}
                          </a>
                        ) : (
                          <p className="text-gray-700 dark:text-gray-200 text-sm">{comment.comment}</p>
                        )}
                        {comment.attachmentUrl && (
                          <div className="mt-2">
                            {comment.attachmentType === "image" ? (
                              <div>
                                <img
                                  src={comment.attachmentUrl}
                                  alt={t("attachment")}
                                  className="max-w-full h-auto rounded-lg max-h-32 object-contain"
                                />
                                <a
                                  href={comment.attachmentUrl}
                                  download={comment.attachmentName}
                                  className="text-[#5932EA] hover:text-[#4a28c7] text-xs font-medium mt-1 inline-block"
                                >
                                  {t("download")}
                                </a>
                              </div>
                            ) : (
                              <a
                                href={comment.attachmentUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-[#5932EA] hover:text-[#4a28c7] text-sm"
                              >
                                {comment.attachmentName || comment.attachmentUrl}
                              </a>
                            )}
                          </div>
                        )}
                        <div className="flex items-center gap-2 mt-2 pt-2 border-t border-gray-200 dark:border-gray-600 text-xs text-gray-500 dark:text-gray-400">
                          <span className="font-medium text-gray-600 dark:text-gray-300">{comment.userName}</span>
                          <span>{new Date(comment.timestamp).toLocaleString()}</span>
                          <button
                            onClick={() => handleDeleteComment(comment.id)}
                            className="ml-auto text-red-500 hover:text-red-600"
                          >
                            {t("delete")}
                          </button>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-gray-500 dark:text-gray-400 text-sm text-center py-6">{t("noNotesYet")}</p>
              )}
            </div>
          </div>

          {/* Metronome Controls */}
          <div className="bg-white dark:bg-gray-800 p-5 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700">
            <h2 className="text-lg font-bold text-gray-800 dark:text-gray-100 mb-4">{t("metronome")}</h2>
            <div className="flex items-center gap-3">
              <label className="text-sm text-gray-600 dark:text-gray-300">{t("bpm")}:</label>
              <input
                type="number"
                value={bpm}
                onChange={(e) => setBpm(parseInt(e.target.value) || 120)}
                min={40}
                max={240}
                className="border border-gray-200 dark:border-gray-600 rounded-xl px-4 py-2 w-20 text-center bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-[#5932EA] focus:border-transparent"
              />
              <button
                onClick={toggleMetronome}
                className="px-4 py-2 rounded-xl bg-[#5932EA] text-white text-sm font-medium hover:bg-[#4a28c7] transition-colors"
              >
                {isPlaying ? t("stop") : t("start")}
              </button>
            </div>
          </div>

          {/* Add Note Modal */}
          <AnimatedModal
            isOpen={isNotesModalOpen}
            onClose={() => setIsNotesModalOpen(false)}
          >
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-md p-5 border border-gray-100 dark:border-gray-700">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100">
                    {t("addNote") || "Add note"}
                  </h3>
                  <button
                    onClick={() => setIsNotesModalOpen(false)}
                    className="p-2 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                  >
                    <HiOutlineXMark className="w-5 h-5" />
                  </button>
                </div>
                <textarea
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  placeholder={t("newNotes")}
                  rows={3}
                  maxLength={400}
                  className="w-full px-4 py-3 text-sm border border-gray-200 dark:border-gray-600 rounded-xl text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-900 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#5932EA] focus:border-transparent resize-none mb-3"
                />
                <div className="flex items-center gap-2 mb-4">
                  <label className="flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400 cursor-pointer hover:text-[#5932EA] transition-colors">
                    <HiOutlinePaperClip className="w-4 h-4" />
                    <input
                      type="file"
                      ref={fileInputRef}
                      accept="image/*, audio/*, application/pdf, application/msword, .docx, .ppt, .pptx"
                      onChange={(e) => setAttachment(e.target.files?.[0] || null)}
                      className="hidden"
                    />
                    {t("attachment") || "Attach"}
                  </label>
                  {attachmentError && (
                    <p className="text-red-500 text-xs">{attachmentError}</p>
                  )}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setIsNotesModalOpen(false)}
                    className="flex-1 px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                  >
                    {t("cancel") || "Cancel"}
                  </button>
                  <button
                    onClick={async () => {
                      if (newComment.trim() || attachment) {
                        await handleAddComment();
                        setIsNotesModalOpen(false);
                      }
                    }}
                    disabled={!newComment.trim() && !attachment}
                    className="flex-1 bg-[#5932EA] text-white text-sm px-4 py-2 rounded-lg hover:bg-[#4a28c7] transition-colors font-medium"
                  >
                    {t("addNotes")}
                  </button>
                </div>
            </div>
          </AnimatedModal>
          </div>
        </div>
      </div>

      {showPresentationMode &&
        id &&
        song &&
        chords &&
        createPortal(
          <PresentationMode
            songs={[
              {
                id,
                title: song.title || "",
                artist: song.artist || "",
                chords,
              },
            ]}
            organizationId={organizationId}
            onClose={() => setShowPresentationMode(false)}
          />,
          document.body
        )}
    </div>
  );
};

export default ViewSong;
